import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-search-results',
  standalone: false,
  templateUrl: './search-results.component.html',
  styleUrl: './search-results.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SearchResultsComponent implements OnChanges {
  @Input() filteredProducts: any[] = [];
  @Input() isSearching = false;
  @Input() searchError = false;
  @Input() searchQuery = '';
  @Output() productClick = new EventEmitter<any>();
  @Output() viewAllResults = new EventEmitter<string>();
  @Output() browseCategories = new EventEmitter<void>();

  constructor(private router: Router) { }

  ngOnChanges(changes: SimpleChanges): void {
    // Component changes handled
  }

  trackByProduct(index: number, product: any): any {
    return product?.id || index;
  }

  onProductClick(product: any): void {
    this.productClick.emit(product);
  }

  /**
   * Navigate to products page with search query
   */
  onViewAllResults(): void {
    if (this.searchQuery.trim()) {
      // Navigate to products page with search query
      this.router.navigate(['/products'], {
        queryParams: { search: this.searchQuery.trim() }
      });
      // Emit the event to let parent components know
      this.viewAllResults.emit(this.searchQuery.trim());
    }
  }

  /**
   * Navigate to the main marketplace page where categories are shown
   */
  onBrowseCategories(): void {
    // Navigate to product list page (allow user to browse categories/filters)
    this.router.navigate(['/products']);
    // Notify parent so it can clear/hide the search dropdown
    this.browseCategories.emit();
  }

  /**
   * Get the first available image from product documents
   * Uses the same logic as admin and product details components
   */
  getFirstAvailableImage(product: any): string | null {
    // Priority 1: Check colors[0].documents first (same as product details)
    if (product?.colors && Array.isArray(product.colors) && product.colors.length > 0) {
      const firstColor = product.colors[0];
      if (firstColor?.documents && Array.isArray(firstColor.documents)) {
        // Sort color documents by their order field
        const sortedColorDocuments = [...firstColor.documents].sort((a: any, b: any) => {
          const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
          const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
          return orderA - orderB;
        });

        const firstValidColorDocument = sortedColorDocuments.find((doc: any) => {
          const isValid = doc && doc.url && doc.url.trim() !== '';
          const isImage = this.isImageUrl(doc?.url);
          return isValid && isImage;
        });

        if (firstValidColorDocument) {
          return firstValidColorDocument.url;
        }
      }
    }

    // Priority 2: Check main documents array (fallback)
    if (product?.documents && Array.isArray(product.documents)) {
      // Sort documents by their order field (same as product form)
      const sortedDocuments = [...product.documents].sort((a: any, b: any) => {
        const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
        const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
        return orderA - orderB;
      });

      // Try to find the best image by looking for specific patterns in labels
      const imageDocuments = sortedDocuments.filter((doc: any) => {
        const isValid = doc && doc.url && doc.url.trim() !== '';
        const isImage = this.isImageUrl(doc?.url);
        return isValid && isImage;
      });

      // Look for documents with specific labels that might indicate main product images
      const preferredLabels = ['main', 'primary', 'product', 'front', 'cover', 'hero'];
      const preferredDocument = imageDocuments.find((doc: any) =>
        preferredLabels.some(label =>
          doc?.label?.toLowerCase().includes(label)
        )
      );

      if (preferredDocument) {
        return preferredDocument.url;
      }

      // If no preferred document found, look for documents that don't have video-related labels
      const nonVideoDocument = imageDocuments.find((doc: any) => {
        const label = doc?.label?.toLowerCase() || '';
        const isVideoLabel = label.includes('video') || label.includes('youtube') || label.includes('demo') || label.endsWith('.mp4') || label.endsWith('.mov') || label.endsWith('.avi');
        return !isVideoLabel;
      });

      if (nonVideoDocument) {
        return nonVideoDocument.url;
      }

      // Fallback to first valid image
      if (imageDocuments.length > 0) {
        return imageDocuments[0].url;
      }
    }

    // Priority 3: Check other colors if first color didn't have images
    if (product?.colors && Array.isArray(product.colors) && product.colors.length > 1) {
      for (let i = 1; i < product.colors.length; i++) {
        const color = product.colors[i];
        if (color?.documents && Array.isArray(color.documents)) {
          const sortedColorDocuments = [...color.documents].sort((a: any, b: any) => {
            const orderA = a.order !== null && a.order !== undefined ? a.order : 999;
            const orderB = b.order !== null && b.order !== undefined ? b.order : 999;
            return orderA - orderB;
          });

          const firstValidColorDocument = sortedColorDocuments.find((doc: any) => {
            const isValid = doc && doc.url && doc.url.trim() !== '';
            const isImage = this.isImageUrl(doc?.url);
            return isValid && isImage;
          });

          if (firstValidColorDocument) {
            return firstValidColorDocument.url;
          }
        }
      }
    }

    // Priority 4: Check other possible image fields
    if (product?.image && product.image.trim() !== '') {
      return product.image;
    }

    if (product?.images && Array.isArray(product.images) && product.images.length > 0) {
      const firstValidImage = product.images.find((img: any) => img && img.trim() !== '');
      if (firstValidImage) {
        return firstValidImage;
      }
    }

    return null;
  }

  /**
   * Check if a URL is an image
   */
  private isImageUrl(url: string): boolean {
    if (!url) return false;

    // Common image file extensions
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg', '.tiff', '.ico', '.avif'];

    const lowerUrl = url.toLowerCase().split('?')[0];

    // Check file extension
    const hasImageExtension = imageExtensions.some(ext =>
      lowerUrl.endsWith(ext.toLowerCase())
    );

    // Check for common image hosting services
    const isImageHosting = url.includes('imagekit.io') ||
      url.includes('cloudinary.com') ||
      url.includes('imgur.com') ||
      url.includes('amazonaws.com') ||
      url.includes('googleapis.com');

    // Check for data URLs (base64 images)
    const isDataUrl = url.startsWith('data:image/');

    // Exclude video URLs
    const isVideoUrl =
      url.includes('youtu.be') ||
      url.includes('youtube.com') ||
      url.includes('vimeo.com') ||
      lowerUrl.endsWith('.mp4') ||
      lowerUrl.endsWith('.mov') ||
      lowerUrl.endsWith('.avi');

    return (hasImageExtension || isImageHosting || isDataUrl) && !isVideoUrl;
  }
  /**
   * Clean up a variant value by removing the label if it's repeated at the start
   */
  getCleanValue(value: string, label: string): string {
    if (!value || !label) return value;
    
    const normalizedLabel = label.toLowerCase().replace(/:$/, '').trim();
    const normalizedValue = value.toLowerCase().trim();
    
    if (normalizedValue.startsWith(normalizedLabel)) {
      let cleaned = value.substring(normalizedLabel.length).trim();
      if (cleaned.startsWith(':')) {
        cleaned = cleaned.substring(1).trim();
      }
      return cleaned || value;
    }
    
    return value;
  }
}
